/* -------------------------- Design imports start -------------------------- */
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement } from "react";
import LogTool from '../../../logger/logTools';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '../../../utils/context';
import { createAddress, getPKfromSelf, isPrivileged } from '../../../utils/functions';
import InfoPanel from '../../../components/layout/InfoPanel';
import { Chip, Grid } from '@mui/material';
import Button from '../../../components/Button';
import InfoField from '../../../components/inputs/InfoField';
import moment from 'moment';
import { Check, Close } from '@mui/icons-material';
import { type Contract } from "../utils/types";
import { APIContractStatusEnum } from "../../../generated-types";
import { User } from "../../../utils/types";
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  setOpenFilesDrawer: (open: boolean) => void;
  infoData: Contract | undefined;
  title?: string;
  disabled?: boolean;
}

type ContentProps = {
  setOpenFilesDrawer: (open: boolean) => void;
  infoData: Contract | undefined;
}

export function ContractInfoPanelContent(props: ContentProps): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const { setOpenFilesDrawer, infoData } = props
  const log = new LogTool({ context: 'ContractInfoPanel', enable: true, logLevel: 'warn' })
  const { t } = useTranslation()
  const { user } = useUserContext()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */

  const renderContractStatus = (status: APIContractStatusEnum | null | undefined): string | number => {
    if(status === undefined || status === null) return t("common:content.label.notAvailable")
    switch (status) {
      case APIContractStatusEnum.NUMBER_0:
        return t("common:content.label.created")
      case APIContractStatusEnum.NUMBER_1:
        return t("common:content.label.productionOrdersCreated")
      case APIContractStatusEnum.NUMBER_2:
        return t("common:content.label.inProgress")
      case APIContractStatusEnum.NUMBER_3:
        return t("common:content.label.shipped")
      case APIContractStatusEnum.NUMBER_4:
        return t("common:content.label.completed")
      case APIContractStatusEnum.NUMBER_5:
        return t("common:content.label.paid")
      case APIContractStatusEnum.NUMBER_6:
        return t("common:content.label.archived")
      case APIContractStatusEnum.NUMBER_7:
        return t("common:content.label.trash")
      case APIContractStatusEnum.NUMBER_8:
        return t("common:content.label.deleted")
      default:
        return status
    }
  }

  const renderAddress = (apiAddress: any): React.ReactNode => {
    const address = createAddress(apiAddress);
    return (
        <>
            {address?.address1}, {address?.address2} {address?.address3}<br />
            {address?.zipCode} {address?.city}<br />
            {address?.country} <br />
            {address?.info}
        </>
    )
}
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  /* ------------------------- Pre render actions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  if (!infoData) return <></>

  log.debug("InfoData -> ", infoData)
  return (
    <>
      <Grid container spacing={2} style={{ padding: "20px" }}>
        <Grid item xs={12}>
          <InfoField
            label={t("contract:content.label.contractNumber")}
            fullWidth
            value={infoData?.self && getPKfromSelf(infoData?.self)}
          />
        </Grid>
        {infoData.offer && typeof infoData.offer !== "string" && (
          <Grid item xs={12}>
            <InfoField
              label={t("contract:content.label.offerNumber")}
              fullWidth
              value={infoData?.offer?.self && getPKfromSelf(infoData?.offer.self)}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <InfoField
            label={t("common:content.label.createdAt")}
            fullWidth
            value={moment(infoData?.createdAt).format('DD.MM.YYYY')}
          />
        </Grid>
        {isPrivileged(user, "STAFF") && (
          <Grid item xs={12}>
            <InfoField
              label={t("common:content.label.contractStatus")}
              fullWidth
              value={renderContractStatus(infoData?.contractStatus)}
            />
          </Grid>
        )}
        {infoData.price && (
          <Grid item xs={12}>
            <InfoField
              label={t("common:content.label.price")}
              fullWidth
              value={infoData?.price ?
                Object.keys(infoData?.price).map(p => `${(infoData.price as any)[p]} ${t("request:content.label.pieces")}: ${p} €`) :
                t("common:content.label.notAvailable")}
            />
          </Grid>
        )}
        {infoData.request && typeof infoData.request !== "string" && (
          <Grid item xs={12}>
            <InfoField
              label={t("common:content.label.certificate")}
              fullWidth
              value={infoData?.request?.certificate}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <InfoField
            label={t("common:content.label.comment")}
            fullWidth
            value={infoData?.comment}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t("common:content.label.desiredDeliveryDate")}
            fullWidth
            value={infoData?.desiredDelivery ? moment(infoData?.desiredDelivery).format('DD.MM.YYYY') : '-'}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t("common:content.label.latestDeliveryDate")}
            fullWidth
            value={infoData?.latestDelivery ? moment(infoData?.latestDelivery).format('DD.MM.YYYY') : '-'}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t("common:content.label.shippingAddress")}
            fullWidth
            value={infoData?.shippingAddress ? renderAddress(infoData?.shippingAddress) : t("common:content.label.notAvailable")}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t("common:content.label.billingAddress")}
            fullWidth
            value={infoData?.billingAddress ? renderAddress(infoData?.billingAddress) : t("common:content.label.notAvailable")}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t("common:content.label.priorityShipping")}
            fullWidth
            value={infoData?.priorityShipping ? <Check /> : <Close />}
          />
        </Grid>
        {isPrivileged(user, "STAFF") && (<Grid item xs={12}>
          <InfoField
            label={t('common:content.label.editors')}
            fullWidth
            value={""}
          />
          {infoData?.editors && infoData.editors.length > 0 && (infoData.editors as User[]).map((editor: User) => {
            return <Chip key={editor.id} label={`${editor.firstName} ${editor.lastName}`} sx={{marginLeft: "10px"}} />
          })}
        </Grid>)}
        <Grid item xs={12}>
          <Button
            id="showFilesButton"
            fullWidth
            variant="contained"
            onClick={() => {
              setOpenFilesDrawer(true)
            }}
          >
            {t('common:interaction.button.showFiles')}
          </Button>
        </Grid>
      </Grid>
    </>
  )
}

export default function ContractInfoPanel(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const { open, setOpen, setOpenFilesDrawer, infoData, title, disabled = true } = props
  const { t } = useTranslation()
  /* --------------------------- Non state data end --------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  if (!infoData) return <></>

  return (
    <InfoPanel
      open={open}
      setOpen={setOpen}
      label={title || t("common:content.label.contractInformation")}
      allowEdit={false}
      disabled={disabled}
    >
      <ContractInfoPanelContent
        setOpenFilesDrawer={setOpenFilesDrawer}
        infoData={infoData}
      />
    </InfoPanel>
  )
}

